import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CustomMap from 'client/components/MapPreview/CustomMap/CustomMap';
import { Feature } from 'ol';
import VectorSource from 'ol/source/Vector';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector.js';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { ASSET_TYPES } from 'client/utils/constants/assetTypes';
import { borderStyle } from 'client/pages/MapPage/layers/styles/aoiStyle';
import { useBaseMap } from 'client/pages/MapPage/layers/sources/BaseMap';
import View from 'ol/View';
import { getCenter } from 'ol/extent';
import styles from './MapPreview.module.scss';
import useAriaZoomControls from 'client/utils/map/applyAriaZoomControls';
import { useDarkMode } from 'client/utils/helpers/DarkMode/useDarkMode';
import { defaults as controlDefaults } from 'ol/control';
import { defaults as interactionDefaults } from 'ol/interaction';
import { getView, scaleToResolution } from './mapPreviewUtils';

// Conversion constant: number of inches in a meter, based on OpenLayers' feet-to-meter ratio

// Formula: resolution = scale / (dpi * metersPerUnit * inchesPerMeter)
// - scale: denominator of the scale (e.g., 1250 for 1:1250)
// - dpi: dots per inch (screen resolution)
// - metersPerUnit: conversion factor for projection units to meters
// - inchesPerMeter: number of inches in a meter

function MapPreview ({ aoi, featuresByAssetType, disableInteraction = false, scale = null, width = null, height = null, forceLightMode = false }) {
    const [map, setMap] = useState();
    const mapRef = useRef();
    const baseMapSource = useBaseMap();
    const [baseMapAdded, setBaseMapAdded] = useState(false);
    const hierarchy = useSelector(state => state.layers?.result?.hierarchy);
    const [highContrastPref] = useDarkMode();
    const highContrast = forceLightMode ? false : highContrastPref;
    const additionalBaseMapClass = highContrast ? styles.highContrastBaseMap : '';

    const getOrAddLayer = (layerId, initialProperties) => {
        const layer = map.getLayerById(layerId);
        const layerExists = Boolean(layer);

        if (layerExists) {
            return layer;
        }

        const vectorLayer = new VectorLayer(initialProperties);
        vectorLayer.setProperties(initialProperties);
        vectorLayer.setStyle(initialProperties.style);
        vectorLayer.set('id', layerId);

        map.addLayer(vectorLayer);

        return vectorLayer;
    };

    const setupMap = () => {
        // This has to be done in a useEffect otherwise the map simply doesn't render
        // I think this is due to openlayers being called before proj4 has registered the projection
        const controls = disableInteraction
            ? controlDefaults({
                attribution: false,
                zoom: false
            })
            : controlDefaults();
        const interactions = disableInteraction
            ? []
            : interactionDefaults();

        const map = new CustomMap({
            view: new View(),
            controls,
            interactions
        });

        setMap(map);

        if (mapRef.current) {
            map.setTarget(mapRef.current);
        }

        return () => map.setTarget(null);
    };
    useEffect(setupMap, []);

    const applyBaseMapLayer = () => {
        if (!map || !baseMapSource || baseMapAdded) {
            return;
        }

        const baseLayer = new TileLayer({
            id: 'BaseMap',
            source: baseMapSource,
            className: additionalBaseMapClass
        });

        const view = getView(baseMapSource.projection.getCode());
        map.setView(view);
        map.addLayer(baseLayer);
        setBaseMapAdded(true);
    };
    useEffect(applyBaseMapLayer, [map, baseMapSource, highContrast]);

    const applyAOILayer = () => {
        if (!map || !baseMapAdded || !aoi) {
            return;
        }

        const aoiSource = new VectorSource({ features: [aoi] });

        const layer = getOrAddLayer('aoi', {
            zIndex: 999,
            style: borderStyle,
            className: additionalBaseMapClass
        });

        layer.setSource(aoiSource);
        const aoiExtent = aoiSource.getExtent();
        if (aoiExtent[0] !== Infinity) {
            if (scale) {
                // If scale is provided, calculate the center and set resolution based on scale
                const center = getCenter(aoiExtent);
                const projection = map.getView().getProjection().getCode();
                const resolution = scaleToResolution(scale, projection);
                map.getView().setCenter(center);
                map.getView().setResolution(resolution);
            } else {
                // Default behavior: fit to extent
                map.getView().fit(aoiExtent, { padding: [50, 50, 50, 50] });
            }
        }
    };
    useEffect(applyAOILayer, [map, baseMapAdded, aoi, highContrast, scale]);

    const applyFeaturesLayer = () => {
        if (!map || !baseMapAdded || !hierarchy || !featuresByAssetType) {
            return;
        }
        hierarchy.leaves().forEach(node => {
            const { assetType, checked, id, zIndex, style } = node.data;
            const features = featuresByAssetType[assetType] || null;
            const shouldInvertLayer = assetType === ASSET_TYPES.OBSERVATION ? highContrast : false;

            const layer = getOrAddLayer(id, {
                visible: checked,
                zIndex,
                style: feature => style(feature), // actively don't want sector styling on mini map
                className: shouldInvertLayer ? additionalBaseMapClass : null
            });

            layer.setSource(new VectorSource({ features }));
        });
    };
    useEffect(applyFeaturesLayer, [map, baseMapAdded, hierarchy, featuresByAssetType]);

    // Update map size when dimensions change
    useEffect(() => {
        if (map && (width || height)) {
            setTimeout(() => {
                map.updateSize();
            }, 100);
        }
    }, [map, width, height]);

    // Apply Aria labels to Zoom Controls for screen-reader accessibility
    useAriaZoomControls();

    const containerStyle = {
        height: height ? `${height}px` : '300px',
        width: width ? `${width}px` : '100%'
    };

    return (
        <div ref={mapRef} style={containerStyle} className={styles.mapContainer} tabIndex={'0'} />
    );
}

MapPreview.propTypes = {
    ...defaultAriaProps,
    aoi: PropTypes.instanceOf(Feature).isRequired,
    featuresByAssetType: PropTypes.object,
    disableInteraction: PropTypes.bool,
    scale: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    forceLightMode: PropTypes.bool
};

export default MapPreview;
